import { Component, Host, h, Prop, Listen, State, Event, EventEmitter, Method, Element } from '@stencil/core';
import { User } from './types/user.types';
import { ConsentManager } from './consent-manager/consent-manager';
import { HttpClient } from './helpers/http-client';
import { isOnlineSupportApplication } from './sew-ws-header.helpers';
import { LogoSvg, ProjectMetaData } from './types/project-metadata.types';
import store from './store/store';
import { BasketLink } from './types/basket-links.types';
import { BasketItem } from './types/basket.types';
import { DirectLinks } from './types/direct-links.types';
import { Labels } from './types/labels.types';
import { Breadcrumb, NavHomeItem, NavItem } from './types/navigation.types';
import { Permission, PermissionStatus, Permissions } from './types/permission.types';
import { getCurrentMediaQuery } from '../../../utils/getCurrentMediaQuery';
import { LabelParser } from './helpers/labels.helpers';
import { E_SEW_BUTTON_HIERARCHY, E_SEW_BUTTON_SIZE } from '../../sew-button/sew-button.types';
import { Tracking } from './tracking/tracking';
import { getSearchUrl } from '../sew-ws-search/sew-ws-search.helper';
import QRCodeStyling from 'qr-code-styling';
import { ApiScope, SewUser, ApiTokenEvent } from '@sew/auth-service';
import { loadClarityScript, loadCognigyWebchatWidget, loadMarketoMunchkin } from './helpers/script.helpers';
import {
    CMS_SAP_API_NAME,
    countryToLangMap,
    domainToCountryCodeMap,
    getAPIBaseUrlFromCaasBaseUrl,
    getCaasBaseUrl,
    getCmsClientId,
    getEnvironmentFromCaasBaseUrl,
    getSapScope,
} from './helpers/utils.helper';
import { AuthService } from './helpers/auth.service';
import { from, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    tag: 'sew-ws-header',
    styleUrl: 'sew-ws-header.scss',
    shadow: true,
})
export class SewWsHeader {
    @Element() host: HTMLSewWsHeaderElement;
    @Event() userChange: EventEmitter<SewUser | User | null>;
    @Event() languageSwitch: EventEmitter<string>;
    @Event() countryShort: EventEmitter<string>;
    @Event() cmsProject: EventEmitter<string>;
    // initComplete is fired when user and language are set
    @Event() initComplete: EventEmitter<void>;
    // labelsChange is fired when labels have been fetched from caas
    @Event() labelsChange: EventEmitter<Labels>;
    @Event() projectMetaDataChange: EventEmitter<ProjectMetaData>;
    @Event() permissionChange: EventEmitter<Permissions>;
    @Event() componentDidRenderEvent: EventEmitter<void>;
    /**
     * Tracking Service
     * can be undefined if tracking is not allowed.
     */
    @Event() getTrackingService: EventEmitter<Tracking | undefined>;

    @Event() authInstance: EventEmitter<AuthService>;
    @Event() accessToken: EventEmitter<ApiTokenEvent>;

    @Prop({ reflect: true }) transparent = false;
    @Prop({ reflect: true }) transparentDesktopOnly = false;

    /**
     * Base url used for all requests against WZ.
     * Should be set to one of the country domains depending on environment.
     * E.g. for german website:
     * DEV -> https://www.sew-d.de
     * QA -> https://www.sew-q.de
     * PROD -> https://www.sew-eurodrive.de
     *
     * Input is not required if this component is use in a country domain or a subdomain of the country domain.
     *
     * @example https://www.sew-d.de
     */
    @Prop({ attribute: 'baseUrl' }) baseUrl: string;

    /**
     * Base url for caas requests.
     * Optional. If environment is set, the caasBaseUrl will be calculated by the header.
     *
     * @example https://caas.sew-eurodrive.com
     */
    @Prop({ attribute: 'caasBaseUrl' }) caasBaseUrl: string | null = null;

    /**
     * Base url for api-calls to SAP-WZ requests.
     * Optional. If environment is set, the apiBaseUrl will be calculated by the header.
     *
     * @example https://api.sew-eurodrive.com
     */
    @Prop({ attribute: 'apiBaseUrl' }) apiBaseUrl: string | null = null;

    /**
     * Country Code of the CMS Project
     * @example for german project: de
     */
    @Prop({ attribute: 'countryCode' }) countryCode: string;

    /**
     * Current language of the header.
     * If no language is passed, the header will take the user selected language.
     * If no user is logged in, it will take the default language of the web page (e.g. for France FR_FR)
     *
     * @exampel for german: DE_DE
     */
    @Prop({ attribute: 'selectedLanguage' }) selectedLanguage: string;

    /**
     * Stage in which the header is implemented.
     * Possible values are "DEV", "QA" and "PROD"
     * Default value is "PROD" Stage (do not set default on this prop)
     *
     * @example "PROD"
     *
     */
    @Prop({ attribute: 'environment' }) environment: 'DEV' | 'QA' | 'PROD';

    /**
     * Flag to set preview for caas, to enable wysiwyg for creators.
     * If isPreview is true, the selectedLanguage and the countryCode Input have to be set.
     * Default is set to false.
     *
     * @example true
     */
    @Prop({ attribute: 'isPreview' }) isPreview = false;

    /**
     * Flag to set the usage of the consent manager.
     * If true or undefined the cookie banner and the tracking cookies will get managed by the header.
     */
    @Prop({ attribute: 'useCMP' }) useCMP: boolean | undefined;

    /**
     * Flag to set the usage of tracking.
     * If true all the requried tracking scripts will load on init.
     */
    @Prop({ attribute: 'useTracking' }) useTracking = false;

    /**
     * Flag to set the usage of cognigy webchat widget.
     * Default is set to true.
     *
     * If true and the related property in projectMetaData is also true, then all the cognigy web chats scripts will load on init.
     * If false, the cognigy web chats will not be loaded on init, even if the related property in projectMetaData is also true.
     *
     * In addition, the user's consent may also be required in the cookies.
     */
    @Prop({ attribute: 'useWebchat' }) useWebchat = false;

    /**
     * ID of navi item.
     * If set, the navi items will be scanned for given id and if found, the breadcrumbs will be displayed
     */
    @Prop({ attribute: 'breadcrumbId' }) breadcrumbId: string;

    /**
     * Default can be overwritten if a deprecated page needs this
     */
    @Prop({ attribute: 'zIndex' }) zIndex = '100';

    /**
     * Flag to hide the login.
     *
     * @default false
     */
    @Prop({ attribute: 'hideLogin' }) hideLogin = false;

    /**
     * Flag to hide the basket.
     *
     * @default false
     */
    @Prop({ attribute: 'hideBasket' }) hideBasket = false;

    /**
     * Flag to set header to a minimal version - removes links and shrinks the size as if scrolled
     */
    @Prop({ reflect: true }) minimalized = false;

    /**
     * Flag to set header to a small version similar to mq1
     */
    @Prop({ reflect: true }) small = false;

    /**
     * This is read only and determines if the header is fully initialized.
     */
    @Prop({ reflect: true }) breadcrumbReady = false;

    /**
     * # OAuth-Param
     * This parameter is used to set the clientId of the application.
     */
    @Prop({ reflect: true }) clientId = undefined;

    /**
     * # OAuth-Param
     * This parameter is used to set the scopes of the application.
     * (clientId-Parameter needed)
     */
    @Prop({ reflect: true }) scopes: ApiScope[] = [];

    /**
     * # OAuth-Param
     * This parameter is used to set the redirect Url of the application.
     * Do not redirect on this page, because the header forwards the user automatically to the login-page.
     */
    @Prop({ reflect: true }) redirectUri = '';

    /**
     * # OAuth-Param
     * This parameter is used to set the clientId of the application.
     */
    @Prop({ reflect: true }) postLogoutRedirectUri = '';

    /**
     * # OAuth-Param
     * This parameter is used to set the clientId of the application.
     * (clientId-Parameter needed)
     */
    @Prop({ reflect: true }) policy = 'B2C_1A_SEW_SUSI_HRD';

    /**
     * # OAuth-Param
     * This parameter is used to set the Login for OIDC Logon of SSR-Applications.
     * When client-ID ist set, the loginUrl is not used.
     */
    @Prop({ reflect: true }) loginUrl = '';

    /**
     * # OAuth-Param
     * This parameter is used to set the Logout for OIDC of SSR-Applications.
     * When client-ID ist set, the logout is not used.
     */
    @Prop({ reflect: true }) logoutUrl = '';

    /**
     * Hides the Breadcrumb if set to true
     * Default is set to false.
     */
    @Prop() hideBreadcrumb = false;

    @State() isScrolled = false;
    @State() labels: Labels;
    @State() user: SewUser | User | null = null;
    @State() isNavOpen = false;
    @State() basketItems: BasketItem[] = [];
    @State() directLinks: DirectLinks | null = null;
    @State() basketLinks: BasketLink[] | null = null;
    @State() svgIcon: LogoSvg | null = null;
    @State() svgLogoMarkup: string | null;
    @State() latestSearchResults: string[] | undefined;
    @State() hasMainNavigation: boolean;
    @State() breadcrumbs: Breadcrumb[] = [];
    @State() userName = '';
    @State() mainNavigationFetched = false;
    @State() private notPermittedRole: Permission | null = null;
    @State() private basketItemCount = '0';
    @State() private tracker?: Tracking;
    @State() private authService?: AuthService;
    @State() navHomeItemRoot: NavItem;
    @State() firstNavBranch: NavItem;
    @State() private navHomeItem: NavItem;

    @Listen('scroll', { target: 'window' })
    handleScroll(): void {
        if (this.minimalized) return;

        this.isScrolled = window.scrollY > 0;
    }

    @Listen('resize', { target: 'window' })
    checkMainNavigationVisibility(): void {
        // ignore this case because of the scrollbar
        // security check for mainNavigationRef, because of reported bug
        if (this.isNavOpen || !this.mainNavigationRef) return;

        this.hasMainNavigation =
            this.mainNavigationRef.scrollWidth === this.mainNavigationRef.clientWidth &&
            getCurrentMediaQuery() >= 3 &&
            !this.small;

        // set transparent depending on media query
        if (this.transparentDesktopOnly) {
            this.transparent = getCurrentMediaQuery() > 1;
        }
    }

    @Listen('counter')
    onCounter(e: CustomEvent<string>): void {
        this.basketItemCount = e.detail;
    }

    @Listen('searchTermChange', { target: 'window' })
    onSearchTermChange(e: CustomEvent): void {
        this.storeSearchHistory(e.detail);
    }

    private httpClient: HttpClient;
    private userInitComplete = false;
    private languageInitComplete = false;
    private permissionsInitComplete = false;
    private permissions: Permission[] | null = null;
    private usePropertyInputLanguage = false;
    private urlLanguage: string | null;
    private labelParser: LabelParser;
    private openedFlyout: HTMLSewWsFlyoutElement | null = null;
    private profileFlyout: HTMLSewWsFlyoutElement;
    private countryLanguageFlyout: HTMLSewWsFlyoutElement;
    private basketFlyout: HTMLSewWsFlyoutElement;
    private searchFlyout: HTMLSewWsFlyoutElement;
    private localSwitchElement: HTMLSewWsLocaleSwitchElement;
    private navigationRef: HTMLSewWsNavigationElement;
    private toggleBtnRef: HTMLButtonElement;
    private searchComponent: HTMLSewWsSearchElement;
    private notificationContainer: HTMLSewWsNotificationsElement;
    private mainNavigationRef: HTMLElement;
    private qrcodeElement: HTMLDivElement;
    private navigateAfterLoginUrl?: string;
    private hasNoBreadcrumb = false;
    private suppressBreadcrumb: boolean = false;
    private breadcrumbBelowStage: boolean = false;
    private wzApiTokenName = CMS_SAP_API_NAME;
    private initCompleted: boolean = false;
    private openNavCategoryIndex: number = -1;

    private readonly restOfTheWorldCountryCode = 'ot';

    @Method()
    public async openBasket(): Promise<void> {
        this.basketFlyout.openFlyout();
    }

    @Method()
    public async openLogin(url?: string): Promise<void> {
        this.profileFlyout.openFlyout();
        this.navigateAfterLoginUrl = url;
    }

    public async componentWillLoad(): Promise<void> {
        // ensure page is scrolled to top on load
        window.scrollTo(0, 0);

        // scroll delayed to hash if available
        const hash = window.location.hash.replace('#', '');

        if (hash) {
            setTimeout(() => {
                const target = document.getElementById(hash);
                if (target) {
                    target.scrollIntoView({ behavior: 'smooth' });
                }
            }, 1000);
        }

        /**
         * Add global method to update basket and display it.
         * Added this method to Utils.fire in driveguide-min.js. So old applications don't have to change anything
         * and basket stills works.
         *
         * For future development. This should be replaced with web socket technology
         */
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).displayHeaderNotification = this.displayHeaderNotification;

        store.onChange('language', language => {
            this.selectedLanguage = language;
            this.handleLanguageChange(language);
        });

        store.onChange('labels', labels => {
            this.labels = labels;
            this.labelParser = new LabelParser(this.labels);
            this.labelsChange.emit(labels);
        });

        store.onChange('permissions', () => {
            this.updateNavigationPermissions();
        });

        store.onChange('projectMetaData', projectMetaData => {
            this.svgIcon = projectMetaData.logoSvg;
            if (this.svgIcon.logo && this.svgIcon.logo.length > 0) {
                this.httpClient.fetchSvgLogo(this.svgIcon.logo).then(svg => (this.svgLogoMarkup = svg));
            }
            if (projectMetaData.cmp_id && this.useCMP !== false) {
                this.implementCookieBanner(projectMetaData.cmp_id, projectMetaData.cmp_src);
            }
            if (this.useTracking) {
                this.tracker = Tracking.getInstance(
                    this.baseUrl,
                    this.useCMP,
                    projectMetaData.et_secure_code,
                    projectMetaData.eTrackerCookiesLifetimeDays,
                    projectMetaData.isEtrackerActivated
                );
                this.tracker.setUserGuid(
                    this.user ? (this.user as SewUser).id || (this.user as User).userGuid : ''
                );
                this.getTrackingService.emit(this.tracker);
            }
            if (projectMetaData.apiPortalConfig?.enabled) {
                this.httpClient.setApiPortalConfig(projectMetaData.apiPortalConfig);
            }
            if (projectMetaData.marketo?.enabled) {
                loadMarketoMunchkin(this.useCMP, projectMetaData.marketo);
            }
            if (
                projectMetaData.webchatWidgetConfig?.enabled &&
                (this.useWebchat || isOnlineSupportApplication(window.location.pathname))
            ) {
                loadCognigyWebchatWidget(
                    this.useCMP,
                    projectMetaData.webchatWidgetConfig.endpointUrl,
                    projectMetaData.webchatWidgetConfig.scriptSrc
                );
            }
            if (projectMetaData.clarity?.enabled) {
                loadClarityScript(this.useCMP, projectMetaData.clarity.code);
            }
            this.userName = this.getUserName(projectMetaData.welcomePlaceholder);

            this.projectMetaDataChange.emit(projectMetaData);
        });
        //caasBaseUrl is required to calculate the environment
        if (!this.environment && this.caasBaseUrl) {
            this.environment = getEnvironmentFromCaasBaseUrl(this.caasBaseUrl);
        }

        if (!this.caasBaseUrl) {
            this.caasBaseUrl = getCaasBaseUrl(this.environment);
        }

        if (!this.apiBaseUrl) {
            this.apiBaseUrl = getAPIBaseUrlFromCaasBaseUrl(this.caasBaseUrl);
        }

        this.baseUrl = this.baseUrl ?? window.location.origin;
        this.httpClient = new HttpClient(this.baseUrl, this.caasBaseUrl, this.isPreview, this.countryCode);

        this.usePropertyInputLanguage = this.selectedLanguage && this.selectedLanguage.length > 0;
        if (this.usePropertyInputLanguage) {
            this.setInitialLanguage();
        }

        this.getUrlLanguage();
        this.checkForBreadcrumbSuppression();

        // initialize login asynchronously to avoid blocking the rendering
        this.initLogin().then();
    }

    private async initLogin() {
        // DESCHEUS: Workaround DomainInfo - wird nun doppelt gerufen!
        // Das ist notwendig, weil für den Auth Service der countryCode benötigt wird.
        // Vermutlich Lösung (DEHAATBI):
        // prüfen ob countryCode schon da ist (weil als property gesetzt) und falls nicht
        // den kompletten fetchDomainInfo call vorziehen, damit er zumindest nicht nochmal gemacht wird am Ende
        try {
            const domainInfoData = await this.httpClient.fetchDomainInfo();
            if (domainInfoData !== null) {
                this.countryCode = domainInfoData.country.toLowerCase();
                this.setSpecialCountryCodeForGB();
            }
        } catch (error) {}

        if (this.clientId) {
            sessionStorage.setItem('oidc', 'true');
        } else {
            const useOidc = await this.httpClient.fetchUseOidc();
            if (useOidc) {
                sessionStorage.setItem('oidc', 'true');
            } else {
                sessionStorage.setItem('oidc', 'false');
            }
        }

        if (this.clientId) {
            const sapScope = getSapScope(this.environment);
            const existingApi = this.scopes.find(scope => scope.scopes.some(s => s === sapScope));
            if (existingApi) {
                this.wzApiTokenName = existingApi.api;
            } else {
                this.scopes.push({ api: CMS_SAP_API_NAME, scopes: ['openid', sapScope] });
            }
            this.authService = AuthService.getInstance(
                this.clientId,
                this.redirectUri,
                this.postLogoutRedirectUri,
                this.selectedLanguage,
                this.countryCode,
                this.environment,
                this.scopes,
                this.policy
            );
        } else if (sessionStorage.getItem('oidc') === 'true') {
            this.scopes.push({ api: CMS_SAP_API_NAME, scopes: ['openid', getSapScope(this.environment)] });
            this.authService = AuthService.getInstance(
                getCmsClientId(this.environment),
                '/',
                '',
                this.selectedLanguage,
                this.countryCode,
                this.environment,
                this.scopes,
                this.policy
            );
        }

        this.fetchDomainInfo();
        this.fetchUserLogin();
    }

    private checkForBreadcrumbSuppression() {
        this.suppressBreadcrumb = !!document.querySelector('sew-ws-stage-parallax');
    }

    private implementCookieBanner(cmpId: string, cmpSrc: string): void {
        const consentManager = ConsentManager.getInstance();
        consentManager.loadConsentManager(cmpId, cmpSrc);
    }

    private getUrlLanguage() {
        try {
            const params = new Proxy(new URLSearchParams(window.top.location.search), {
                get: (searchParams, prop) => searchParams.get(prop.toString()),
            });
            this.urlLanguage = params['language'];
        } catch (e) {
            console.log('window.top.location cannot be accessed');
        }
    }

    private onCloseNavigation = (e: CustomEvent<{ close: boolean; path: EventTarget[] }>): void => {
        const path = e.detail.path;
        const close = e.detail.close;

        if ((path && path.every(el => el !== this.mainNavigationRef)) || (!path && close)) {
            this.closeNavigation();
        }
    };

    private displayHeaderNotification = () => {
        // Special for basket page, because function is called on init and it's possible that labelParser is not defined.
        // Depends on dictionary request via caas.
        if (typeof this.labelParser === 'undefined') {
            setTimeout(() => {
                this.displayHeaderNotification();
            }, 300);

            return;
        }

        this.fetchBasketContent().then(() => {
            const newCount = this.calcBasketItemsCount();

            const event = new CustomEvent('addNotification', {
                detail: {
                    headline: this.labelParser
                        .getLabel('CMS_HEADER_BASKET_COUNT')
                        .replace('{{count}}', newCount.toString()),
                    message: this.labelParser.getLabel('CMS_HEADER_BASKET_OPEN'),
                    link: `<a href="${
                        this.baseUrl
                    }/os/c/main.do?viewset=BASKET&amp;country=${this.countryCode.toUpperCase()}&amp;language=${this.selectedLanguage.toUpperCase()}">${this.labelParser.getLabel(
                        'CMS_HEADER_BASKET_OPEN'
                    )}</a>`,
                    duration: 5000,
                },
            });
            this.notificationContainer.dispatchEvent(event);
        });
    };

    private fetchPermissionDependedData() {
        this.httpClient.fetchPermissions().then((permissions: Permission[]) => {
            this.permissions = permissions;
            store.state.permissions = permissions;
            if (this.userInitComplete) {
                this.permissionChange.emit(permissions);
                this.permissionsInitComplete = true;
                this.handleInitComplete();
            }
        });
    }

    private fetchMainNavigation() {
        this.httpClient.fetchNavigation().then((navHomeItemRoot: NavHomeItem) => {
            store.state.navigation = navHomeItemRoot;
            this.updateNavigationPermissions();
        });
    }

    private updateNavigationPermissions() {
        const caasNavigationRoot = store.state.navigation;
        if (caasNavigationRoot) {
            this.navHomeItemRoot = {
                ...caasNavigationRoot,
                level: 0,
                children: this.mapChildren(caasNavigationRoot, 0),
                login_required: false,
                hideWhenLoggedIn: false,
            };
            this.setETrackerObjects(this.navHomeItemRoot, '');

            // reopen the last opened category
            if (this.openNavCategoryIndex === -1) {
                this.navHomeItem = this.navHomeItemRoot;
            } else {
                this.navHomeItem = this.navHomeItemRoot.children[this.openNavCategoryIndex];
            }

            if (this.breadcrumbId && !this.suppressBreadcrumb) {
                this.setInitialBreadcrumbs();
            } else {
                this.hasNoBreadcrumb = true;
            }

            this.mainNavigationFetched = true;
        }
    }

    private setETrackerObjects(item: NavItem, currentPath: string) {
        item.children.forEach(child => {
            child.eTrackerObject = `${currentPath}/${child.name}`;
            this.setETrackerObjects(child, child.eTrackerObject);
        });
    }

    private checkHasPermission(item: {
        isLocked?: boolean;
        role_required?: string;
        login_required?: boolean;
    }): boolean {
        return item.role_required !== undefined
            ? this.permissions !== null && this.permissions.some(entry => entry.auth === item.role_required)
            : true;
    }

    private mapChildren(kid: NavItem, level: number): NavItem[] {
        level++;

        return kid.children
            ? kid.children
                  .filter(child => !(child.hideWhenLoggedIn && this.user !== null))
                  .map(child => ({
                      ...child,
                      level: level,
                      hasPermission: this.checkHasPermission(child),
                      isLocked: child.login_required ? this.user === null : false,
                      children: this.mapChildren(child, level),
                  }))
            : [];
    }

    private getUserName(welcomePlaceholder: string) {
        return this.user !== null
            ? welcomePlaceholder
                  .replace(
                      '[# user.forename #]',
                      (this.user as SewUser).firstName || (this.user as User).firstname
                  )
                  .replace('[# user.name #]', (this.user as SewUser).lastName || (this.user as User).lastname)
            : '';
    }

    private setInitialBreadcrumbs() {
        const path: NavItem[] = [];
        this.findBreadcrumbsPathinNavigation({ children: this.navHomeItem.children } as NavItem, path);
        this.firstNavBranch = path[0];
        this.breadcrumbs = path.map(item => ({ url: item.link, linkName: item.name }));

        if (!this.breadcrumbs || this.breadcrumbs.length === 0) {
            this.hasNoBreadcrumb = true;
        }
    }

    private findBreadcrumbsPathinNavigation(menuItem: NavItem, path: NavItem[] = []): boolean {
        if (menuItem.uid === this.breadcrumbId) {
            return true;
        } else if (menuItem.children) {
            for (const child of menuItem.children) {
                path.push(child);
                if (this.findBreadcrumbsPathinNavigation(child, path)) {
                    return true;
                }
                path.splice(path.length - 1, 1);
            }
        } else {
            return false;
        }
    }

    private async fetchBasketContent() {
        return this.httpClient
            .fetchBasketContent()
            .then(basketContent => (this.basketItems = basketContent.content));
    }

    private fetchAccessTokensForScopes() {
        return forkJoin(
            this.scopes.map(scope =>
                from(this.authService.getTokenForApi(scope.api)).pipe(
                    map(token => {
                        if (!token) {
                            if (scope.api === this.wzApiTokenName) {
                                this.completeUserInit(null);
                            }
                            console.error(`Failed to fetch token for API "${scope.api}".`);
                            return null;
                        }
                        return { api: scope.api, token };
                    })
                )
            )
        );
    }

    private async fetchUserLogin() {
        if (this.hideLogin || this.isPreview) {
            this.completeUserInit(null);
            return;
        }

        if (sessionStorage.getItem('oidc') === 'true') {
            const sapSession = await this.httpClient.fetchUserLogin();

            try {
                if (sapSession.isloggedin) {
                    this.httpClient.fetchUserData().then(user => {
                        this.completeUserInit(user);
                    });
                    if (this.wzApiTokenName === CMS_SAP_API_NAME && this.scopes.length === 1) {
                        return;
                    }
                }

                await this.authService.init();
                this.authInstance.emit(this.authService);

                this.fetchAccessTokensForScopes().subscribe(apiTokens => {
                    // Example: Process the tokens with their corresponding APIs
                    apiTokens.forEach(tokenData => {
                        if (!tokenData) {
                            return;
                        }

                        const { api, token } = tokenData;

                        if (api === this.wzApiTokenName && !sapSession.isloggedin) {
                            this.httpClient.fetchUserData(token).then(user => {
                                this.httpClient.initializeSapSession();
                                if (
                                    window.location.pathname.startsWith('/os/c/') ||
                                    window.location.pathname.startsWith('/os/dud/') ||
                                    window.location.pathname.startsWith('/os/catalog/')
                                ) {
                                    location.reload();
                                } else {
                                    this.completeUserInit(user);
                                }
                            });
                        }

                        if (this.wzApiTokenName !== CMS_SAP_API_NAME) {
                            this.accessToken.emit({ api, token });
                        }
                    });
                });
            } catch (error) {
                this.completeUserInit(null);
            }
        } else {
            this.httpClient.fetchUserLogin().then(({ isloggedin }) => {
                if (isloggedin) {
                    this.httpClient.fetchUserData().then(user => {
                        this.completeUserInit(user);
                    });
                } else {
                    this.completeUserInit(null);
                }
            });
        }
    }

    private completeUserInit(user: SewUser | User | null) {
        this.user = user;
        this.userInitComplete = true;
        this.handleUserChange(user);
        store.set('user', user);
        this.handleInitComplete();
    }

    private fetchDomainInfo() {
        // domain info makes no sense in preview mode
        if (this.isPreview) {
            return;
        }

        this.httpClient.fetchDomainInfo().then(data => {
            if (data === null) {
                this.useFallbackDomainInfo();
            } else {
                store.set('domainInfo', data);
                this.countryCode = this.countryCode ?? data.country.toLowerCase();
                this.selectedLanguage = this.selectedLanguage ?? data.defaultLanguage;
            }
            this.setSpecialCountryCodeForGB();
            this.countryShort.emit(this.countryCode);
            this.cmsProject.emit(`SEW${this.countryCode.toUpperCase()}`);

            if (this.countryCode && this.selectedLanguage) {
                store.set('language', this.selectedLanguage);
            }
            this.setInitialLanguage();
            this.handleInitComplete();
        });
    }

    private useFallbackDomainInfo() {
        console.error('Using fallback values because domain info could not be read.');

        if (window.location.host === 'www.sew-d.de' || window.location.host === 'www.sew-q.de') {
            this.countryCode = this.countryCode ?? 'de';
        } else {
            const pattern: RegExp = /^([a-z]{2})\.sew-(qa|d)\.com$/;
            const match: RegExpMatchArray | null = window.location.host.match(pattern);

            if (match) {
                this.countryCode = this.countryCode ?? match[1];
            }
        }

        this.countryCode = this.countryCode ?? (domainToCountryCodeMap[window.location.host] || 'unknown');
        this.selectedLanguage = this.selectedLanguage ?? (countryToLangMap[this.countryCode] || 'unknown');
    }

    private setSpecialCountryCodeForGB() {
        if (this.countryCode === 'gb') {
            this.countryCode = 'uk';
        }
    }

    private fetchProjectMetaData(): void {
        this.httpClient.fetchProjectMetaData().then(projectMetaData => {
            store.set('projectMetaData', {
                author: projectMetaData.author,
                cmp_id: projectMetaData.cmp_id,
                cmp_src: projectMetaData.cmp_src,
                contacts_worldwide: projectMetaData.contacts_worldwide,
                country_selector_url: projectMetaData.country_selector_url,
                description: projectMetaData.description,
                et_secure_code: projectMetaData.et_secure_code,
                eTrackerCookiesLifetimeDays: projectMetaData.eTrackerCookiesLifetimeDays ?? null,
                isEtrackerActivated: projectMetaData.isEtrackerActivated,
                fav_icon: projectMetaData.fav_icon,
                keywords: projectMetaData.keywords,
                logo: projectMetaData.logo,
                logoSvg: projectMetaData.logoSvg,
                welcomePlaceholder: projectMetaData.welcomePlaceholder,
                apiPortalConfig: projectMetaData.apiPortalConfig,
                webchatWidgetConfig: projectMetaData.webchatWidgetConfig,
                clarity: projectMetaData.clarity,
                marketo: projectMetaData.marketo,
                displayBlogPostFooter: projectMetaData.displayBlogPostFooter,
            });
        });
    }

    private fetchLabels(): void {
        this.httpClient.fetchLabels().then(data => store.set('labels', data.dictionary));
    }

    private fetchRecommendations(searchTerm: string): void {
        this.httpClient
            .fetchSearchRecommendations(this.selectedLanguage, this.countryCode, searchTerm)
            .then(recommendations => this.searchComponent.setRecommendations(recommendations))
            .catch(() => {
                this.searchComponent.setRecommendations([]);
            });
    }

    private fetchBasketLinks() {
        if (this.basketLinks !== null) {
            return;
        }

        this.httpClient.fetchBasketLinks().then(basketLinks => {
            this.basketLinks = basketLinks.map(basketLink => {
                basketLink.hasPermission = this.checkHasPermission(basketLink);
                basketLink.isLocked = basketLink.login_required ? this.user === null : false;
                return basketLink;
            });
        });
    }

    private fetchDirectLinks(): void {
        if (this.directLinks !== null) {
            return;
        }

        this.httpClient.fetchDirectLinks().then(directLinks => (this.directLinks = directLinks));
    }

    private setSearchHistory(): void {
        if (this.user) {
            if (this.latestSearchResults === undefined) {
                this.httpClient
                    .fetchSearchHistory()
                    .then(response => (this.latestSearchResults = response.slice(0, 5)));
            }
        } else {
            this.resetSearchHistory();
        }
    }

    private resetSearchHistory(): void {
        this.latestSearchResults = undefined;
    }

    private storeSearchHistory(searchTerm: string): void {
        if (!this.user) {
            window.location.href = getSearchUrl(this.baseUrl, this.selectedLanguage, searchTerm);

            return;
        }

        this.httpClient
            .storeSearchHistory(searchTerm)
            .then(
                () => (window.location.href = getSearchUrl(this.baseUrl, this.selectedLanguage, searchTerm))
            )
            .catch(
                () => (window.location.href = getSearchUrl(this.baseUrl, this.selectedLanguage, searchTerm))
            );
    }

    private async logout() {
        if (this.clientId) {
            await this.httpClient.logoutSapSession();
            await this.authService?.logout();
        } else {
            if (this.logoutUrl) {
                window.location.href = `${this.logoutUrl}${window.location.href}`;
            } else {
                if (sessionStorage.getItem('oidc') === 'true') {
                    await this.httpClient.logoutSapSession();
                    await this.authService?.logout();
                    // window.location.href = `/os/c/logout.do?redirect_url=${window.location.href}`;
                } else {
                    window.location.href = `${this.apiBaseUrl}/service/b2c/logout?redirect_url=${window.location.href}`;
                }
            }
        }
    }

    private async login() {
        const currentUrl = encodeURIComponent(window.location.pathname + location.search);
        if (this.clientId) {
            await this.authService?.login();
        } else {
            if (this.loginUrl) {
                window.location.href = `${this.loginUrl}${currentUrl}`;
            } else {
                window.location.href = `/os/c/main.do?viewset=LOGIN&country=${this.countryCode}&language=${this.selectedLanguage}&onsuccess=${currentUrl}`;
            }
        }
    }

    private closeOtherFlyouts(flyout: HTMLSewWsFlyoutElement) {
        if (this.openedFlyout === flyout) {
            return;
        }

        this.openedFlyout && this.openedFlyout.closeFlyout();
        this.openedFlyout = flyout;
    }

    private closeOpenFlyout() {
        this.openedFlyout && this.openedFlyout.closeFlyout();
    }

    private changeLanguage(language: string) {
        this.httpClient.changeLanguageOfBackendUser(language).then(() => {
            store.set('language', language);
        });
    }

    private navigateToBasket() {
        window.location.href = `${this.baseUrl}/os/c/main.do?viewset=BASKET&country=${encodeURIComponent(
            this.countryCode.toUpperCase()
        )}&language=${encodeURIComponent(this.selectedLanguage.toUpperCase())}`;
    }

    private navigateToLoginPage(url: string) {
        window.location.href = `${this.baseUrl}/os/c/main.do?viewset=login&onsuccess=${encodeURIComponent(
            url
        )}`;
    }

    private openCategory = (navItemIndex: number) => {
        const navItem =
            navItemIndex === -1 ? this.navHomeItemRoot : this.navHomeItemRoot.children[navItemIndex];
        if (this.isNavOpen && navItem === this.navHomeItem) return;

        if (this.isNavOpen) {
            this.softClose();
        }
        this.openNavCategoryIndex = navItemIndex;

        setTimeout(
            () => {
                (this.navHomeItem as NavItem) = navItem;
                this.openNavigation();
            },
            this.isNavOpen ? 350 : 0
        );
    };

    private openNavigation() {
        this.isNavOpen = true;
        this.toggleBtnRef.classList.remove('close-animation');
        !this.isScrolled && this.host.classList.add('scrolled');
        document.body.style.setProperty('overflow-y', 'hidden');
    }

    private closeNavigation() {
        this.navHomeItem = null;
        this.navigationRef.classList.add('close-animation');
        this.toggleBtnRef.classList.add('close-animation');
        !this.isScrolled && this.host.classList.remove('scrolled');

        setTimeout(() => {
            this.isNavOpen = false;
            document.body.style.removeProperty('overflow-y');
        }, 300);
    }

    private softClose() {
        this.navHomeItem = null;
        this.navigationRef.classList.add('close-animation');
        this.toggleBtnRef.classList.add('close-animation');

        setTimeout(() => {
            this.isNavOpen = false;
        }, 300);
    }

    private handleInitComplete() {
        if (
            !this.initCompleted &&
            this.userInitComplete &&
            this.languageInitComplete &&
            this.permissionsInitComplete
        ) {
            this.initCompleted = true;
            this.userChange.emit(this.user);
            this.initComplete.emit();
        }
    }

    private setInitialLanguage() {
        // attribute on sew-header html element is the strongest
        if (!this.usePropertyInputLanguage) {
            // Query param from url second strogens
            if (this.urlLanguage) {
                this.selectedLanguage = this.urlLanguage;
                // user languages
            } else if (
                this.user !== null &&
                this.user.language.toUpperCase() !== this.selectedLanguage.toUpperCase()
            ) {
                this.selectedLanguage = this.user.language;
            }
        }

        // updating the language in the store implicitly triggers handleLanguageChange via the store listener
        store.set('language', this.selectedLanguage);
        this.languageInitComplete = true;
    }

    private handleUserChange(user: SewUser | User | null) {
        if (store.get('projectMetaData')) {
            this.userName = this.getUserName(store.get('projectMetaData').welcomePlaceholder);
        }

        if (this.tracker) {
            this.tracker.setUserGuid(user ? (this.user as User).userGuid || (this.user as SewUser).id : '');
        }

        if (user !== null) {
            if (this.navigateAfterLoginUrl && this.navigateAfterLoginUrl.length > 0) {
                window.location.href = this.navigateAfterLoginUrl;

                return;
            }

            if (this.user.language.toUpperCase() !== this.selectedLanguage.toUpperCase()) {
                this.selectedLanguage = user.language;
                store.set('language', user.language);
            } else {
                this.fetchUserDependenedData();
            }

            return;
        }

        this.fetchUserDependenedData();
    }

    private handleLanguageChange(language: string) {
        this.directLinks = null;
        this.basketLinks = null;
        this.fetchProjectMetaData();
        this.fetchMainNavigation();
        this.fetchUserDependenedData();
        this.fetchLabels();
        this.languageSwitch.emit(language);
    }

    private fetchUserDependenedData() {
        this.fetchBasketContent();
        this.fetchPermissionDependedData();
    }

    private calcBasketItemsCount(): number {
        if (!this.basketItems) {
            return 0;
        }

        const count = this.basketItems.reduce(
            (sum, basketItem) => sum + (basketItem.unit !== 'ST' ? 1 : basketItem.quantity),
            0
        );

        return count;
    }

    private handleNotPermittedItemClicked(permission: string) {
        this.httpClient.fetchPermissionStatus(permission).then(response => {
            if (response.status !== PermissionStatus.GRANTED) {
                this.notPermittedRole = response;
            }
        });
    }

    private requestPermission(permission: string) {
        this.httpClient.requestPermission(permission);
        this.notPermittedRole.status = PermissionStatus.REQUESTED;
    }

    private closePermissionDialog() {
        this.notPermittedRole = null;
    }

    componentDidLoad(): void {
        this.minimalized && (this.isScrolled = true);

        const qrCode = new QRCodeStyling({
            width: 100,
            height: 100,
            data: location.href,
            dotsOptions: {
                color: '#34383c',
            },
            backgroundOptions: {
                color: '#ffffff',
            },
        });

        qrCode.append(this.qrcodeElement);
    }

    componentDidRender(): void {
        this.checkMainNavigationVisibility();
        this.moveBreadcrumbBelowStage();

        if ((!this.minimalized && this.breadcrumbs?.length) || this.hasNoBreadcrumb) {
            this.breadcrumbReady = true;
        }

        this.componentDidRenderEvent.emit();
    }

    private moveBreadcrumbBelowStage(): void {
        const stage = document.querySelector('sew-ws-stage, sew-ws-stage-headline-rotation');
        const breadcrumb = this.host.shadowRoot.querySelector('sew-ws-breadcrumb');

        if (this.transparent && stage && breadcrumb) {
            this.breadcrumbBelowStage = true;
            stage.parentNode.insertBefore(breadcrumb, stage.nextSibling);
        }
    }

    render() {
        return (
            <Host
                class={{
                    'scrolled': this.isScrolled,
                    'small': this.small,
                    'breadcrumb-below-stage': this.breadcrumbBelowStage,
                    'has-no-breadcrumb': this.hasNoBreadcrumb,
                }}
            >
                <header style={{ 'z-index': this.zIndex }}>
                    <sew-ws-notifications
                        ref={el => (this.notificationContainer = el as HTMLSewWsNotificationsElement)}
                    />
                    <div class="grid">
                        <div class={{ 'sub-grid': true, 'has-no-main-navigation': !this.hasMainNavigation }}>
                            {this.renderLogo()}
                            <a class="qrcode" href={location.href}>
                                <div ref={el => (this.qrcodeElement = el)} />
                            </a>
                            {!this.minimalized && (
                                <nav
                                    ref={el => (this.mainNavigationRef = el)}
                                    class={{ 'main-navigation': true, 'visible': this.hasMainNavigation }}
                                >
                                    {this.navHomeItemRoot?.children.map((navItem, index) => (
                                        <button
                                            class={{
                                                active:
                                                    this.navHomeItem === navItem ||
                                                    this.firstNavBranch === navItem,
                                            }}
                                            onClick={() => this.openCategory(index)}
                                        >
                                            {navItem.name}
                                        </button>
                                    ))}
                                </nav>
                            )}
                            {!this.minimalized && this.labelParser && (
                                <nav
                                    class={{
                                        'service-navigation': true,
                                        'visible': this.mainNavigationFetched,
                                    }}
                                >
                                    <div class="actions-container">
                                        {this.countryCode !== this.restOfTheWorldCountryCode &&
                                            !this.hideLogin && (
                                                <sew-ws-flyout
                                                    icon={this.user !== null ? 'user-filled' : 'user'}
                                                    icon-label={this.labelParser.getLabel(
                                                        'CMS_HEADER_LOGIN_HEADLINE'
                                                    )}
                                                    ref={el =>
                                                        (this.profileFlyout = el as HTMLSewWsFlyoutElement)
                                                    }
                                                    username={this.userName}
                                                    onClick={() => this.closeOtherFlyouts(this.profileFlyout)}
                                                    onFlyoutClosed={() =>
                                                        (this.navigateAfterLoginUrl = undefined)
                                                    }
                                                >
                                                    <sew-ws-login
                                                        ref={el => el as HTMLSewWsLoginElement}
                                                        baseUrl={this.baseUrl}
                                                        labels={this.labels}
                                                        country={store.get('domainInfo')?.country ?? null}
                                                        language={store.get('language') ?? null}
                                                        user={store.get('user') ?? null}
                                                        onLogout={() => this.logout()}
                                                        onLogin={() => this.login()}
                                                    ></sew-ws-login>
                                                </sew-ws-flyout>
                                            )}
                                        <sew-ws-flyout
                                            icon="globe-america"
                                            icon-label={this.labelParser.getLabel(
                                                'CMS_HEADER_COUNTRY_SELECTION_HEADLINE'
                                            )}
                                            ref={el =>
                                                (this.countryLanguageFlyout = el as HTMLSewWsFlyoutElement)
                                            }
                                            onClick={() => {
                                                this.closeOtherFlyouts(this.countryLanguageFlyout);
                                            }}
                                            onMouseEnter={() => {
                                                this.localSwitchElement.initCountries();
                                            }}
                                        >
                                            <sew-ws-locale-switch
                                                ref={el =>
                                                    (this.localSwitchElement =
                                                        el as HTMLSewWsLocaleSwitchElement)
                                                }
                                                countryCode={this.countryCode}
                                                languageCode={this.selectedLanguage}
                                                countrySelectorUrl={
                                                    store.get('projectMetaData')?.country_selector_url ?? null
                                                }
                                                labels={this.labels}
                                                httpClient={this.httpClient}
                                                isPreview={this.isPreview}
                                                basketHasItems={this.basketItemCount !== '0'}
                                                onSwitchLanguage={ev => this.changeLanguage(ev.detail)}
                                                onClose={() => this.closeOpenFlyout()}
                                            ></sew-ws-locale-switch>
                                        </sew-ws-flyout>
                                        {!this.hideBasket && (
                                            <sew-ws-flyout
                                                icon="shopping-cart"
                                                icon-label={this.labelParser.getLabel(
                                                    'CMS_HEADER_BASKET_HEADLINE'
                                                )}
                                                ref={el => (this.basketFlyout = el as HTMLSewWsFlyoutElement)}
                                                onClick={() => {
                                                    this.closeOtherFlyouts(this.basketFlyout);
                                                    this.fetchBasketLinks();
                                                }}
                                            >
                                                <sew-ws-basket
                                                    basketItems={this.basketItems}
                                                    links={this.basketLinks}
                                                    labels={this.labels}
                                                    onShowBasket={() => this.navigateToBasket()}
                                                ></sew-ws-basket>
                                            </sew-ws-flyout>
                                        )}
                                        <sew-ws-flyout
                                            icon="search"
                                            icon-label={this.labelParser.getLabel(
                                                'CMS_SEARCH_PAGE_TITLE_SEARCH'
                                            )}
                                            fullscreen
                                            ref={el => (this.searchFlyout = el as HTMLSewWsFlyoutElement)}
                                            onClick={() => {
                                                this.closeOtherFlyouts(this.searchFlyout);
                                                this.setSearchHistory();
                                                this.fetchDirectLinks();
                                            }}
                                            class="header-search"
                                        >
                                            <sew-ws-search
                                                ref={el =>
                                                    (this.searchComponent = el as HTMLSewWsSearchElement)
                                                }
                                                baseUrl={this.baseUrl}
                                                language={this.selectedLanguage}
                                                country={this.countryCode}
                                                labels={this.labels}
                                                directLinks={this.directLinks}
                                                latestSearchResults={this.latestSearchResults}
                                                onFetchRecommendation={event =>
                                                    this.fetchRecommendations(event.detail)
                                                }
                                                onStoreSearchHistory={event =>
                                                    this.storeSearchHistory(event.detail)
                                                }
                                            ></sew-ws-search>
                                        </sew-ws-flyout>
                                        <button
                                            class={{
                                                'menu-toggle-btn': !this.isNavOpen,
                                                'menu-close-btn': this.isNavOpen,
                                                'visible': this.hasMainNavigation === false,
                                            }}
                                            onClick={() =>
                                                this.isNavOpen
                                                    ? this.closeNavigation()
                                                    : this.openCategory(-1)
                                            }
                                            ref={ref => (this.toggleBtnRef = ref)}
                                            aria-label={this.labelParser.getLabel(
                                                'CMS_GLOBAL_HEADER_MAIN_NAVIGATION'
                                            )}
                                        >
                                            <sew-icon
                                                width={!this.isNavOpen ? '48' : '48'}
                                                height="48"
                                                icon={!this.isNavOpen ? 'menu' : 'close'}
                                                class="svg"
                                            ></sew-icon>
                                        </button>
                                    </div>
                                </nav>
                            )}
                        </div>
                    </div>

                    {this.isNavOpen && (
                        <sew-ws-navigation
                            ref={ref => (this.navigationRef = ref)}
                            navHomeItem={this.navHomeItem}
                            user={this.user}
                            labels={this.labels}
                            has-main-navigation={this.hasMainNavigation}
                            language={this.selectedLanguage}
                            tracking={this.tracker}
                            onNavigateLogin={e => this.navigateToLoginPage(e.detail)}
                            onNotPermittedItemClicked={notPermittedRole =>
                                this.handleNotPermittedItemClicked(notPermittedRole.detail)
                            }
                            onCloseNavigation={this.onCloseNavigation}
                        ></sew-ws-navigation>
                    )}

                    {this.notPermittedRole && (
                        <sew-dialog
                            ws
                            type="info"
                            close-button="true"
                            headline={this.labelParser.getLabel('CMS_HEADER_NO_PERMISSION_HEADLINE')}
                            open={true}
                            onClose={() => this.closePermissionDialog()}
                            size="xs"
                        >
                            <p>
                                {this.notPermittedRole.status === PermissionStatus.NO_PERMISSION &&
                                    this.labelParser.getLabel('CMS_HEADER_NO_PERMISSION_TEXT')}
                                {this.notPermittedRole.status === PermissionStatus.REQUESTED &&
                                    this.labelParser.getLabel('CMS_HEADER_PERMISSION_REQUESTED')}
                            </p>
                            <sew-button
                                hierarchy={E_SEW_BUTTON_HIERARCHY.SECONDARY}
                                size={E_SEW_BUTTON_SIZE.S}
                                slot="button"
                            >
                                <button onClick={() => this.closePermissionDialog()}>
                                    {this.labelParser.getLabel('CMS_HEADER_CANCEL')}
                                </button>
                            </sew-button>
                            <sew-button
                                hierarchy={E_SEW_BUTTON_HIERARCHY.PRIMARY}
                                size={E_SEW_BUTTON_SIZE.S}
                                slot="button"
                            >
                                {this.notPermittedRole.status === PermissionStatus.NO_PERMISSION && (
                                    <button
                                        onClick={() => this.requestPermission(this.notPermittedRole.auth)}
                                    >
                                        {this.labelParser.getLabel('CMS_HEADER_REQUEST_PERMISSION')}
                                    </button>
                                )}
                                {this.notPermittedRole.status === PermissionStatus.REQUESTED && (
                                    <button onClick={() => this.closePermissionDialog()}>
                                        {this.labelParser.getLabel('CMS_HEADER_CONFRIM')}
                                    </button>
                                )}
                            </sew-button>
                        </sew-dialog>
                    )}
                </header>
                {!this.transparent && <div class="header-gap"></div>}
                {!this.hideBreadcrumb && !this.minimalized && this.breadcrumbs?.length > 0 && (
                    <sew-ws-breadcrumb links={JSON.stringify(this.breadcrumbs)} no-margin></sew-ws-breadcrumb>
                )}
            </Host>
        );
    }

    private renderLogo = () =>
        this.svgIcon !== null && (
            <div class={'logo' + (this.svgIcon.class.length ? ' ' + this.svgIcon.class : '')}>
                {this.svgIcon.link !== null && this.svgIcon.link.length > 0 && (
                    <a href={this.svgIcon.link} title={this.svgIcon.title} aria-label={this.svgIcon.title}>
                        <div class="svg" innerHTML={this.svgLogoMarkup ?? ''}></div>
                    </a>
                )}
                {this.svgIcon.link === null && (
                    <div class="svg" innerHTML={this.svgLogoMarkup ?? ''} title={this.svgIcon.title}></div>
                )}
            </div>
        );
}
